import { useEffect, useMemo, useRef } from 'react';
import { useLazyQuery } from '@apollo/client';
import { GET_MERCH_RECOMMENDATIONS } from '../queries';
import { FIRST_ORDER_GIFT } from '../constants';
import {
  IGetMerchRecommendationQueryResult,
  IPrismicRichText,
  ISearchProduct,
  IShoppingCart,
} from '@monofront/types';
import { useEligibility } from '../contexts';

export interface IGetRecommendationVariables {
  widgetId: string;
  limit?: number;
  variables: {
    url: string | null;
    sku?: string;
    cart: {
      skus: string[];
    };
    shouldIncludeFreshProducts?: boolean;
  };
}

type UseMerchProductsRecommendationsArgs = {
  widgetId: string | undefined;
  shoppingCart: IShoppingCart;
  // sku to get recommendations from
  sku?: string;
  isMobileApp?: boolean;
  limit?: number;
};

type UseMerchProductsRecommendationsResult = {
  products: ISearchProduct[];
  loading: boolean;
  title?: IPrismicRichText;
  refetchRecommendations: () => void;
  description?: IPrismicRichText;
  id?: string;
};

export const useMerchProductsRecommendations = ({
  widgetId,
  shoppingCart,
  sku,
  isMobileApp = false,
  limit,
}: UseMerchProductsRecommendationsArgs): UseMerchProductsRecommendationsResult => {
  const recommendationsLoaded = useRef<boolean>(false);
  const appliedWidgetId = useRef<string | undefined>(widgetId);

  const [getRecommendations, { data, loading, refetch }] = useLazyQuery<
    IGetMerchRecommendationQueryResult,
    IGetRecommendationVariables
  >(GET_MERCH_RECOMMENDATIONS, {
    ssr: false,
    fetchPolicy: 'cache-first',
  });

  const skusInCart = useMemo(
    () => shoppingCart.products?.map((product) => product.sku) ?? [],
    [shoppingCart],
  );

  const { eligibility } = useEligibility();

  useEffect(() => {
    // Fetch only once automatically, else it will reload the carousels and remove
    // the products which may have been added, causing a less than subpar experience

    if (appliedWidgetId.current !== widgetId) {
      recommendationsLoaded.current = false;
      appliedWidgetId.current = widgetId;
    }

    if (!widgetId || recommendationsLoaded.current) {
      return;
    }

    void getRecommendations({
      variables: {
        widgetId,
        limit,
        variables: {
          url: isMobileApp ? null : window.location.href,
          sku: sku ?? '',
          cart: {
            skus: skusInCart,
          },
          shouldIncludeFreshProducts: Boolean(eligibility.ultraFresh),
        },
      },
    });

    recommendationsLoaded.current = true;
  }, [sku, skusInCart, widgetId, eligibility, limit]);

  const products = useMemo(() => {
    const raw = data?.getRecommendationsV6?.recommendations ?? [];
    const filtered = raw.filter(
      ({ tags }) => !tags.some((tag: string) => tag === FIRST_ORDER_GIFT),
    );
    const sorted = filtered.sort((prev, next) => {
      const prevPosition = data?.getRecommendationsV6?.positions.find(
        (score) => score.sku === prev.sku,
      );
      const nextPosition = data?.getRecommendationsV6?.positions.find(
        (score) => score.sku === next.sku,
      );

      if (prevPosition && nextPosition) {
        return prevPosition.position - nextPosition.position;
      }

      return 0;
    });

    return sorted;
  }, [
    data?.getRecommendationsV6?.recommendations,
    data?.getRecommendationsV6?.positions,
  ]);

  const refetchRecommendations = () => {
    void refetch({
      widgetId,
      limit,
      variables: {
        url: isMobileApp ? null : window.location.href,
        sku,
        cart: {
          skus: skusInCart,
        },
      },
    });
  };

  return {
    products,
    loading,
    refetchRecommendations,
    id: data?.getRecommendationsV6?.id,
    title: data?.getRecommendationsV6?.widget?.title,
    description: data?.getRecommendationsV6?.widget?.description,
  };
};
